import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { ExercisePicker } from './ExercisePicker'
import type { Program, ProgramExercise } from '@/types'

/**
 * In-app manual program builder. Edit-in-place cards double as the preview —
 * what you build is exactly what the athlete sees. Produces a `Program` object
 * (single week for now; "Add week" duplicates the current one for progression).
 */

type EditEx = ProgramExercise & { _id: string }
interface EditDay {
  label: string
  exercises: EditEx[]
}
interface EditWeek {
  days: EditDay[]
}

let _seq = 0
// Random suffix so ids never collide with ids restored from a saved draft.
const uid = () => `e${_seq++}-${Math.random().toString(36).slice(2, 6)}`

const DRAFT_PREFIX = 'fz-builder-draft-'

/**
 * Drop a saved draft. The PARENT calls this once a save has actually succeeded —
 * clearing on click meant a failed save (or a cancelled Assign dialog followed by
 * a tab switch) threw the coach's work away.
 */
export function clearBuilderDraft(key: string) {
  try {
    localStorage.removeItem(DRAFT_PREFIX + key)
  } catch {
    /* storage disabled */
  }
}

interface Draft {
  name: string
  description: string
  weeks: EditWeek[]
  savedAt: number
}

function loadDraft(key: string): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + key)
    if (!raw) return null
    const d = JSON.parse(raw) as Draft
    if (!d || !Array.isArray(d.weeks) || !d.weeks.length) return null
    return d
  } catch {
    return null
  }
}

const blankExercise = (): EditEx => ({ _id: uid(), exercise_id: '', name: '', sets: null, reps: '' })
const blankDay = (n: number): EditDay => ({ label: `Day ${n}`, exercises: [blankExercise()] })

/** Seed the editor from an existing program (import / AI), giving each exercise
 *  a local id so it can be edited/removed. */
function fromProgram(p: Program): EditWeek[] {
  return p.weeks.map((w) => ({
    days: w.days.map((d) => ({
      label: d.label,
      exercises: d.exercises.map((e) => ({ ...e, _id: uid() })),
    })),
  }))
}

export default function ManualBuilder({
  initial,
  onSave,
  onSaveTemplate,
  onUpdate,
  draftKey = 'new',
}: {
  initial?: Program
  onSave?: (p: Program) => void
  onSaveTemplate?: (p: Program) => void
  /** Edit-in-place: save changes back to the existing program. */
  onUpdate?: (p: Program) => void
  /** Auto-save the in-progress program under this key so a tab switch / reload
   *  can't lose it. Unique per new-vs-edit target. */
  draftKey?: string
}) {
  // If an unsaved draft exists for this builder, resume it; otherwise seed from
  // the passed-in program (edit / AI / import) or a blank week.
  const draft = useRef<Draft | null>(loadDraft(draftKey)).current
  const [restoredDraft, setRestoredDraft] = useState(Boolean(draft))

  const [name, setName] = useState(draft?.name ?? initial?.name ?? '')
  const [description, setDescription] = useState(draft?.description ?? initial?.description ?? '')
  const [weeks, setWeeks] = useState<EditWeek[]>(() =>
    draft ? draft.weeks : initial && initial.weeks.length ? fromProgram(initial) : [{ days: [blankDay(1)] }]
  )
  const [wi, setWi] = useState(0)
  const [di, setDi] = useState(0)

  const clearDraft = () => localStorage.removeItem(DRAFT_PREFIX + draftKey)

  // Persist on every edit (debounced) so nothing is lost on tab switch / reload.
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          DRAFT_PREFIX + draftKey,
          JSON.stringify({ name, description, weeks, savedAt: Date.now() } satisfies Draft)
        )
      } catch {
        /* storage full / disabled — ignore */
      }
    }, 400)
    return () => clearTimeout(t)
  }, [name, description, weeks, draftKey])

  const discardDraft = () => {
    clearDraft()
    setRestoredDraft(false)
    setName(initial?.name ?? '')
    setDescription(initial?.description ?? '')
    setWeeks(initial && initial.weeks.length ? fromProgram(initial) : [{ days: [blankDay(1)] }])
    setWi(0)
    setDi(0)
  }

  const week = weeks[wi]
  const day = week.days[di]

  const mutateDay = (fn: (d: EditDay) => EditDay) => {
    setWeeks((prev) =>
      prev.map((w, i) =>
        i === wi ? { days: w.days.map((d, j) => (j === di ? fn(d) : d)) } : w
      )
    )
  }

  const updateEx = (id: string, patch: Partial<ProgramExercise>) =>
    mutateDay((d) => ({
      ...d,
      exercises: d.exercises.map((e) => (e._id === id ? { ...e, ...patch } : e)),
    }))

  const addExercise = () => mutateDay((d) => ({ ...d, exercises: [...d.exercises, blankExercise()] }))
  const removeExercise = (id: string) =>
    mutateDay((d) => ({ ...d, exercises: d.exercises.filter((e) => e._id !== id) }))
  // Swap an exercise with its neighbour — for slotting in something forgotten.
  const moveExercise = (id: string, dir: -1 | 1) =>
    mutateDay((d) => {
      const i = d.exercises.findIndex((e) => e._id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= d.exercises.length) return d
      const next = [...d.exercises]
      ;[next[i], next[j]] = [next[j], next[i]]
      return { ...d, exercises: next }
    })

  const addDay = () => {
    setWeeks((prev) => prev.map((w, i) => (i === wi ? { days: [...w.days, blankDay(w.days.length + 1)] } : w)))
    setDi(week.days.length)
  }

  const addWeek = () => {
    // Duplicate the current week (deep copy with fresh ids) for progression.
    const copy: EditWeek = {
      days: week.days.map((d) => ({ ...d, exercises: d.exercises.map((e) => ({ ...e, _id: uid() })) })),
    }
    setWeeks((prev) => [...prev, copy])
    setWi(weeks.length)
    setDi(0)
  }

  const build = (): Program => ({
    id: 'draft-' + Date.now(),
    name: name.trim() || 'Untitled Program',
    description: description.trim() || undefined,
    source: initial?.source ?? 'manual',
    goal_tags: initial?.goal_tags,
    overview: initial?.overview,
    weeks: weeks.map((w, i) => ({
      week: i + 1,
      days: w.days.map((d) => ({
        label: d.label,
        exercises: d.exercises
          .filter((e) => e.name.trim())
          .map(({ _id, ...rest }) => ({
            ...rest,
            exercise_id: rest.exercise_id || rest.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          })),
      })),
    })),
  })

  const totalExercises = weeks.reduce(
    (n, w) => n + w.days.reduce((m, d) => m + d.exercises.filter((e) => e.name.trim()).length, 0),
    0
  )

  // The draft is only cleared by the parent once the save succeeds.
  const handleSave = () => onSave?.(build())
  const handleUpdate = () => onUpdate?.(build())
  const handleSaveTemplate = () => onSaveTemplate?.(build())

  return (
    <div className="animate-fade-in">
      {restoredDraft && (
        <div className="mb-3 flex items-center gap-3 rounded-btn border border-data/30 bg-data/10 px-4 py-2.5 text-sm text-data">
          <span className="flex-1">Restored your unsaved draft.</span>
          <button onClick={discardDraft} className="font-semibold text-muted underline hover:text-text">
            Discard
          </button>
        </div>
      )}

      {/* Program meta */}
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Program name"
        className="w-full bg-transparent font-display text-3xl tracking-wide text-text outline-none placeholder:text-subtle"
      />
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Short description (optional)"
        className="mt-1 w-full bg-transparent text-sm text-muted outline-none placeholder:text-subtle"
      />

      {/* Week selector */}
      <div className="mt-5 flex items-center gap-2">
        {weeks.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setWi(i)
              setDi(0)
            }}
            className={cn(
              'rounded-full border px-3 py-1 font-condensed text-sm font-bold uppercase tracking-wide transition-all',
              i === wi ? 'gold-gradient border-transparent text-black' : 'border-border text-muted hover:text-gold'
            )}
          >
            Week {i + 1}
          </button>
        ))}
        <button onClick={addWeek} className="rounded-full border border-dashed border-border-strong px-3 py-1 text-sm text-muted hover:border-gold hover:text-gold">
          + Week
        </button>
      </div>

      {/* Day tabs */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {week.days.map((d, i) => (
          <button
            key={i}
            onClick={() => setDi(i)}
            className={cn(
              'rounded-full border px-4 py-1.5 font-display text-base tracking-wide transition-all',
              i === di ? 'border-gold text-gold' : 'border-border text-subtle hover:text-gold'
            )}
          >
            {d.label}
          </button>
        ))}
        <button onClick={addDay} className="rounded-full border border-dashed border-border-strong px-4 py-1.5 text-sm text-muted hover:border-gold hover:text-gold">
          + Day
        </button>
      </div>

      {/* Editable day name */}
      <input
        value={day.label}
        onChange={(e) => mutateDay((d) => ({ ...d, label: e.target.value }))}
        className="mt-4 w-full bg-transparent font-condensed text-lg font-bold text-text outline-none"
      />

      {/* Exercise rows */}
      <div className="mt-2 space-y-3">
        {day.exercises.map((ex, i) => (
          <ExerciseEditor
            key={ex._id}
            ex={ex}
            onChange={(patch) => updateEx(ex._id, patch)}
            onRemove={() => removeExercise(ex._id)}
            onMoveUp={i > 0 ? () => moveExercise(ex._id, -1) : undefined}
            onMoveDown={i < day.exercises.length - 1 ? () => moveExercise(ex._id, 1) : undefined}
          />
        ))}
      </div>

      <Button variant="secondary" block className="mt-3" onClick={addExercise}>
        + Add exercise
      </Button>

      {/* Save bar */}
      <div className="sticky bottom-0 -mx-5 mt-6 flex items-center gap-3 border-t border-border bg-surface/95 px-5 py-3 backdrop-blur">
        <span className="text-xs text-muted">
          {weeks.length} week{weeks.length > 1 ? 's' : ''} · {totalExercises} exercise{totalExercises !== 1 ? 's' : ''}
        </span>
        {onSaveTemplate && (
          <Button
            variant="secondary"
            className="ml-auto"
            disabled={!name.trim() || totalExercises === 0}
            onClick={handleSaveTemplate}
          >
            Save as Template
          </Button>
        )}
        {onUpdate ? (
          <Button
            className={onSaveTemplate ? '' : 'ml-auto'}
            disabled={!name.trim() || totalExercises === 0}
            onClick={handleUpdate}
          >
            Save Changes
          </Button>
        ) : (
          <Button
            className={onSaveTemplate ? '' : 'ml-auto'}
            disabled={!name.trim() || totalExercises === 0}
            onClick={handleSave}
          >
            Save &amp; Assign
          </Button>
        )}
      </div>
    </div>
  )
}

function ExerciseEditor({
  ex,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  ex: EditEx
  onChange: (patch: Partial<ProgramExercise>) => void
  onRemove: () => void
  /** Absent when the exercise is already first / last. */
  onMoveUp?: () => void
  onMoveDown?: () => void
}) {
  const arrow =
    'flex h-5 w-7 items-center justify-center rounded text-sm leading-none text-muted hover:bg-surface-3 hover:text-gold disabled:pointer-events-none disabled:opacity-20'
  const field =
    'bg-white/[.04] border border-border-strong rounded-lg px-2.5 h-10 text-sm text-text outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 placeholder:text-subtle'
  return (
    <div className="rounded-card border border-border bg-gradient-to-b from-surface-2 to-surface p-3">
      <div className="flex items-center gap-2">
        <div className="flex flex-shrink-0 flex-col">
          <button onClick={onMoveUp} disabled={!onMoveUp} className={arrow} aria-label="Move up" title="Move up">
            ▲
          </button>
          <button onClick={onMoveDown} disabled={!onMoveDown} className={arrow} aria-label="Move down" title="Move down">
            ▼
          </button>
        </div>
        <input
          value={ex.group ?? ''}
          onChange={(e) => onChange({ group: e.target.value || null })}
          placeholder="A1"
          className={cn(field, 'w-12 text-center font-mono')}
        />
        <ExercisePicker
          value={ex.name}
          exerciseId={ex.exercise_id}
          onSelect={(id, name) => onChange({ exercise_id: id, name })}
          onFreeText={(name) => onChange({ name, exercise_id: '' })}
        />
        <button onClick={onRemove} className="px-1.5 text-lg text-subtle hover:text-danger" aria-label="Remove">
          ✕
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <input
          type="number"
          value={ex.sets ?? ''}
          onChange={(e) => onChange({ sets: e.target.value ? Number(e.target.value) : null })}
          placeholder="Sets"
          className={field}
        />
        <input value={ex.reps ?? ''} onChange={(e) => onChange({ reps: e.target.value })} placeholder="Reps" className={field} />
        <input value={ex.load ?? ''} onChange={(e) => onChange({ load: e.target.value })} placeholder="Load" className={field} />
        <input value={ex.tempo ?? ''} onChange={(e) => onChange({ tempo: e.target.value })} placeholder="Tempo" className={field} />
        <input value={ex.rest ?? ''} onChange={(e) => onChange({ rest: e.target.value })} placeholder="Rest" className={field} />
      </div>
      <input
        value={ex.notes ?? ''}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="Coaching cue / notes (optional)"
        className={cn(field, 'mt-2 h-10 w-full')}
      />
      <input
        value={ex.video ?? ''}
        onChange={(e) => onChange({ video: e.target.value || null })}
        placeholder="▶ Demo video — paste a YouTube link (optional)"
        className={cn(field, 'mt-2 h-10 w-full')}
      />
    </div>
  )
}
